import React, { useState, useMemo } from 'react';
import { formatMediaUrl } from '../../config';
import {
  makeStyles,
  Divider,
  Typography,
  Box,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@material-ui/core';
import {
  FileCopyOutlined,
  Language,
  Phone,
  Launch,
  InfoOutlined,
  ListOutlined,
  CheckCircleOutline,
  Payment,
  TouchApp,
  Close as CloseIcon,
  GetApp,
  PictureAsPdf,
  InsertDriveFile
} from '@material-ui/icons';
import MarkdownWrapper from '../MarkdownWrapper';
import toast from 'react-hot-toast';

const useStyles = makeStyles((theme) => ({
  templateCard: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  headerMediaContainer: {
    width: '100%',
    maxHeight: '260px',
    overflow: 'hidden',
    borderRadius: '8px',
    marginBottom: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
  },
  headerMediaImage: {
    width: '100%',
    maxHeight: '260px',
    objectFit: 'cover',
    borderRadius: '8px',
  },
  headerMediaVideo: {
    width: '100%',
    maxHeight: '260px',
    borderRadius: '8px',
  },
  headerDocumentCard: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    border: theme.palette.type === 'dark' ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
    },
  },
  documentIcon: {
    fontSize: 34,
    color: '#E53E3E',
    marginRight: 10,
    flexShrink: 0,
  },
  documentDetails: {
    flex: 1,
    minWidth: 0,
  },
  documentName: {
    fontSize: '0.86rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.type === 'dark' ? '#F1F5F9' : '#1E293B',
  },
  documentSub: {
    fontSize: '0.74rem',
    color: theme.palette.type === 'dark' ? '#94A3B8' : '#64748B',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '0.97rem',
    lineHeight: 1.35,
    marginBottom: '6px',
    color: theme.palette.type === 'dark' ? '#F1F5F9' : '#0F172A',
    wordBreak: 'break-word',
  },
  headerSubtitle: {
    fontSize: '0.82rem',
    fontStyle: 'italic',
    color: theme.palette.type === 'dark' ? '#94A3B8' : '#64748B',
    marginBottom: '6px',
  },
  bodyContent: {
    fontSize: '0.90rem',
    lineHeight: 1.5,
    color: theme.palette.type === 'dark' ? '#E2E8F0' : '#1E293B',
    wordBreak: 'break-word',
    marginBottom: '4px',
  },
  footerText: {
    fontSize: '0.75rem',
    color: theme.palette.type === 'dark' ? '#94A3B8' : '#64748B',
    marginTop: '6px',
    marginBottom: '4px',
    fontStyle: 'italic',
    display: 'block',
  },
  templateBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '4px',
    marginBottom: '6px',
  },
  templateBadge: {
    fontSize: '0.68rem',
    color: theme.palette.type === 'dark' ? '#94A3B8' : '#64748B',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    opacity: 0.85,
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '8px',
    width: '100%',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '7px 12px',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.84rem',
    border: theme.palette.type === 'dark' ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.10)',
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.75)',
    color: theme.palette.primary.main,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
      borderColor: theme.palette.primary.main,
    },
  },
  pixCard: {
    marginTop: '8px',
    padding: '10px 12px',
    borderRadius: '10px',
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.06)',
    border: '1px solid rgba(16, 185, 129, 0.25)',
  },
  pixTitle: {
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#10B981',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '6px',
  },
  dialogSectionHeader: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
    padding: '6px 16px',
    fontWeight: 700,
    fontSize: '0.82rem',
    color: theme.palette.primary.main,
  },
}));

const Template = ({ message, sendReply }) => {
  const classes = useStyles();
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Parse structured data from message
  const templateData = useMemo(() => {
    let headerTitle = null;
    let headerSubtitle = null;
    let bodyText = null;
    let footerText = null;
    let buttons = [];
    let mediaUrl = null;
    let documentName = null;
    let isTemplate = false;

    // 1. Tentar parsear dataJson (formato Baileys e WhatsApp Oficial)
    if (message.dataJson) {
      try {
        const raw = typeof message.dataJson === 'string' ? JSON.parse(message.dataJson) : message.dataJson;
        const msg = raw?.message?.viewOnceMessage?.message ||
                    raw?.message?.viewOnceMessageV2?.message ||
                    raw?.message?.ephemeralMessage?.message ||
                    raw?.message || raw;

        // A. Template Message (Meta API oficial / Baileys)
        const template = msg?.templateMessage?.hydratedTemplate ||
                         msg?.templateMessage?.hydratedFourRowTemplate ||
                         msg?.highlyStructuredMessage?.hydratedHsm?.hydratedTemplate;

        const fourRow = msg?.templateMessage?.fourRowTemplate;

        if (template) {
          isTemplate = true;
          headerTitle = template.hydratedTitleText || null;
          bodyText = template.hydratedContentText || null;
          footerText = template.hydratedFooterText || null;

          if (template.documentMessage) {
            documentName = template.documentMessage.fileName || template.documentMessage.title || 'Documento';
          }

          if (Array.isArray(template.hydratedButtons)) {
            template.hydratedButtons.forEach((btn, idx) => {
              if (btn.quickReplyButton) {
                buttons.push({
                  type: 'QUICK_REPLY',
                  text: btn.quickReplyButton.displayText,
                  id: btn.quickReplyButton.id,
                  index: idx,
                });
              } else if (btn.urlButton) {
                buttons.push({
                  type: 'URL',
                  text: btn.urlButton.displayText,
                  url: btn.urlButton.url,
                  index: idx,
                });
              } else if (btn.callButton) {
                buttons.push({
                  type: 'PHONE_NUMBER',
                  text: btn.callButton.displayText,
                  phone_number: btn.callButton.phoneNumber,
                  index: idx,
                });
              }
            });
          }
        } else if (fourRow) {
          isTemplate = true;
          headerTitle = typeof fourRow.title === 'string' ? fourRow.title : fourRow.title?.text || null;
          bodyText = typeof fourRow.content === 'string' ? fourRow.content : fourRow.content?.text || null;
          footerText = typeof fourRow.footer === 'string' ? fourRow.footer : fourRow.footer?.text || null;

          if (fourRow.documentMessage) {
            documentName = fourRow.documentMessage.fileName || fourRow.documentMessage.title || 'Documento';
          }

          if (Array.isArray(fourRow.buttons)) {
            fourRow.buttons.forEach((btn, idx) => {
              const text = btn.quickReplyButton?.displayText || btn.urlButton?.displayText || btn.callButton?.displayText || btn.buttonText?.displayText;
              if (btn.urlButton?.url) {
                buttons.push({ type: 'URL', text, url: btn.urlButton.url, index: idx });
              } else if (btn.callButton?.phoneNumber) {
                buttons.push({ type: 'PHONE_NUMBER', text, phone_number: btn.callButton.phoneNumber, index: idx });
              } else if (text) {
                buttons.push({ type: 'QUICK_REPLY', text, id: btn.quickReplyButton?.id || idx, index: idx });
              }
            });
          }
        }

        // B. Interactive Message (Meta Cloud API / Baileys)
        const interactive = msg?.interactiveMessage;
        if (interactive) {
          isTemplate = true;
          if (interactive.header?.title) headerTitle = interactive.header.title;
          if (interactive.header?.subtitle) headerSubtitle = interactive.header.subtitle;
          if (interactive.body?.text) bodyText = interactive.body.text;
          if (interactive.footer?.text) footerText = interactive.footer.text;

          if (interactive.header?.documentMessage) {
            documentName = interactive.header.documentMessage.fileName || interactive.header.documentMessage.title || 'Documento';
          }

          if (interactive.nativeFlowMessage?.buttons && Array.isArray(interactive.nativeFlowMessage.buttons)) {
            interactive.nativeFlowMessage.buttons.forEach((btn, idx) => {
              try {
                const params = typeof btn.buttonParamsJson === 'string' ? JSON.parse(btn.buttonParamsJson) : (btn.buttonParamsJson || {});
                const displayText = params.display_text || params.displayText || params.title || btn.name;

                if (btn.name === 'payment_info' || btn.name === 'review_and_pay' || btn.name === 'pix_payment') {
                  buttons.push({
                    type: 'PIX',
                    text: displayText || 'Pagamento PIX',
                    params,
                    index: idx,
                  });
                } else if (btn.name === 'cta_url' && params.url) {
                  buttons.push({
                    type: 'URL',
                    text: displayText,
                    url: params.url,
                    index: idx,
                  });
                } else if (btn.name === 'cta_call' && params.phone_number) {
                  buttons.push({
                    type: 'PHONE_NUMBER',
                    text: displayText,
                    phone_number: params.phone_number,
                    index: idx,
                  });
                } else if (btn.name === 'cta_copy' && (params.copy_code || params.code)) {
                  buttons.push({
                    type: 'COPY_CODE',
                    text: displayText || 'Copiar Código',
                    code: params.copy_code || params.code,
                    index: idx,
                  });
                } else if (btn.name === 'single_select') {
                  buttons.push({
                    type: 'LIST',
                    text: params.title || 'Ver Opções',
                    sections: params.sections || [],
                    index: idx,
                  });
                } else {
                  buttons.push({
                    type: 'QUICK_REPLY',
                    text: displayText,
                    id: params.id || idx,
                    index: idx,
                  });
                }
              } catch (e) {
                buttons.push({
                  type: 'QUICK_REPLY',
                  text: btn.name,
                  id: idx,
                  index: idx,
                });
              }
            });
          }
        }

        // C. Buttons Message
        const buttonsMessage = msg?.buttonsMessage;
        if (buttonsMessage) {
          isTemplate = true;
          if (buttonsMessage.contentText) bodyText = buttonsMessage.contentText;
          if (buttonsMessage.footerText) footerText = buttonsMessage.footerText;
          if (Array.isArray(buttonsMessage.buttons)) {
            buttonsMessage.buttons.forEach((b, idx) => {
              buttons.push({
                type: 'QUICK_REPLY',
                text: b.buttonText?.displayText || b.buttonId,
                id: b.buttonId,
                index: idx,
              });
            });
          }
        }

        // D. List Message
        const listMessage = msg?.listMessage;
        if (listMessage) {
          isTemplate = true;
          if (listMessage.title) headerTitle = listMessage.title;
          if (listMessage.description) bodyText = listMessage.description;
          if (listMessage.footerText) footerText = listMessage.footerText;
          buttons.push({
            type: 'LIST',
            text: listMessage.buttonText || listMessage.title || 'Ver Opções',
            sections: listMessage.sections || [],
            index: 0,
          });
        }
      } catch (err) {
        console.error('Erro ao fazer parse do dataJson no Template:', err);
      }
    }

    // 2. Fallback para formato com separador ||||
    if (message.body && typeof message.body === 'string' && message.body.includes('||||')) {
      const [rawBody, rawButtonsJson] = message.body.split('||||');
      if (!bodyText) bodyText = rawBody.trim();

      if (rawButtonsJson && rawButtonsJson !== '[]' && buttons.length === 0) {
        try {
          let parsed = typeof rawButtonsJson === 'string' ? JSON.parse(rawButtonsJson) : rawButtonsJson;
          if (typeof parsed === 'string') parsed = JSON.parse(parsed);
          const buttonList = Array.isArray(parsed) ? parsed : Object.values(parsed);

          buttonList.forEach((b, idx) => {
            if (b.type === 'URL') {
              buttons.push({ type: 'URL', text: b.text, url: b.url, index: idx });
            } else if (b.type === 'PHONE_NUMBER') {
              buttons.push({ type: 'PHONE_NUMBER', text: b.text, phone_number: b.phone_number, index: idx });
            } else if (b.type === 'COPY_CODE') {
              buttons.push({ type: 'COPY_CODE', text: b.text, code: b.example?.[0] || b.code || '', index: idx });
            } else if (b.text) {
              buttons.push({ type: 'QUICK_REPLY', text: b.text, id: idx, index: idx });
            }
          });
        } catch (e) {
          console.error('Erro ao fazer parse dos botões ||||:', e);
        }
      }
    }

    // 3. Fallback: se bodyText ainda não foi definido, usar message.body limpo
    if (!bodyText && message.body) {
      let cleanBody = message.body;
      cleanBody = cleanBody.replace(/^\[TEMPLATE\]\s*/i, '');
      cleanBody = cleanBody.replace(/^\[INTERATIVA\]\s*/i, '');
      cleanBody = cleanBody.replace(/^\[BUTTON\]\s*/i, '');
      cleanBody = cleanBody.replace(/^\[LIST\]\s*/i, '');
      bodyText = cleanBody.trim();
    } else if (bodyText) {
      bodyText = bodyText.replace(/^\[TEMPLATE\]\s*/i, '')
                         .replace(/^\[INTERATIVA\]\s*/i, '')
                         .replace(/^\[BUTTON\]\s*/i, '')
                         .replace(/^\[LIST\]\s*/i, '')
                         .trim();
    }

    // 4. Detecção de URLs de mídia no texto
    const urlRegex = /(https?:\/\/[^\s]+(\.jpg|\.jpeg|\.png|\.gif|\.webp|\.mp4|\.webm|\.ogg|\.pdf))/i;
    if (bodyText && urlRegex.test(bodyText)) {
      const match = bodyText.match(urlRegex);
      if (match) {
        mediaUrl = match[0];
        bodyText = bodyText.replace(urlRegex, '').trim();
      }
    }

    // Se o backend salvou mediaUrl no message
    if (message.mediaUrl && !mediaUrl) {
      mediaUrl = formatMediaUrl(`public/company${message.companyId}/${message.mediaUrl}`);
    }

    if (!documentName && message.mediaUrl) {
      documentName = message.mediaUrl;
    }

    return {
      headerTitle,
      headerSubtitle,
      bodyText,
      footerText,
      buttons,
      mediaUrl,
      documentName,
      isTemplate: isTemplate || buttons.length > 0 || !!headerTitle,
    };
  }, [message]);

  // Funções de ação para botões
  const handleCopyCode = (code, index) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    toast.success('Código copiado para a área de transferência!');
    setTimeout(() => {
      setCopiedIndex(null);
    }, 2500);
  };

  const handleOpenUrl = (url) => {
    if (!url) return;
    const finalUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCall = (phoneNumber) => {
    if (!phoneNumber) return;
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanNumber}`, '_blank', 'noopener,noreferrer');
  };

  const handleOpenListDialog = (button) => {
    setSelectedList(button);
    setListDialogOpen(true);
  };

  const handleQuickReply = (text) => {
    if (typeof sendReply === 'function') {
      sendReply(text);
    } else {
      toast(`Opção: "${text}"`, { icon: '🔘' });
    }
  };

  const handleDownloadDocument = async (url, filename) => {
    if (!url) return;
    try {
      const res = await fetch(url);
      if (res.ok) {
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename || 'documento';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        return;
      }
    } catch (e) {
      console.warn('Fallback para download direto de documento template:', e);
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'documento';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isImage = (url) => /\.(jpeg|jpg|gif|png|webp)$/i.test(url);
  const isVideo = (url) => /\.(mp4|webm|ogg)$/i.test(url);
  const isPdf = (url) => /\.pdf$/i.test(url);

  const { headerTitle, headerSubtitle, bodyText, footerText, buttons, mediaUrl, documentName } = templateData;

  const hasDocument = mediaUrl && !isImage(mediaUrl) && !isVideo(mediaUrl);

  return (
    <div className={classes.templateCard}>
      {/* Mídia do Cabeçalho se houver (Imagem / Vídeo) */}
      {mediaUrl && (isImage(mediaUrl) || isVideo(mediaUrl)) && (
        <div className={classes.headerMediaContainer}>
          {isImage(mediaUrl) ? (
            <img src={mediaUrl} alt="Mídia do modelo" className={classes.headerMediaImage} />
          ) : (
            <video src={mediaUrl} controls className={classes.headerMediaVideo} />
          )}
        </div>
      )}

      {/* Documento / PDF no Cabeçalho se houver */}
      {hasDocument && (
        <div
          className={classes.headerDocumentCard}
          onClick={() => handleDownloadDocument(mediaUrl, documentName)}
          title="Clique para baixar ou abrir o documento"
        >
          {isPdf(mediaUrl) ? (
            <PictureAsPdf className={classes.documentIcon} />
          ) : (
            <InsertDriveFile className={classes.documentIcon} style={{ color: '#2563EB' }} />
          )}
          <div className={classes.documentDetails}>
            <div className={classes.documentName}>{documentName || 'Documento Anexo'}</div>
            <div className={classes.documentSub}>Clique para abrir / baixar</div>
          </div>
          <GetApp style={{ color: '#64748B', fontSize: 22, marginLeft: 8 }} />
        </div>
      )}

      {/* Título / Cabeçalho */}
      {headerTitle && (
        <Typography className={classes.headerTitle} component="div">
          {headerTitle}
        </Typography>
      )}

      {/* Subtítulo do cabeçalho */}
      {headerSubtitle && (
        <Typography className={classes.headerSubtitle} component="div">
          {headerSubtitle}
        </Typography>
      )}

      {/* Corpo do Texto */}
      {bodyText && (
        <div className={classes.bodyContent}>
          <MarkdownWrapper>{bodyText}</MarkdownWrapper>
        </div>
      )}

      {/* Rodapé */}
      {footerText && (
        <Typography className={classes.footerText} component="div">
          {footerText}
        </Typography>
      )}

      {/* Selo oficial de modelo WhatsApp */}
      <div className={classes.templateBadgeRow}>
        <Tooltip title="Mensagem oficial ou automatizada do WhatsApp">
          <span className={classes.templateBadge}>
            <InfoOutlined style={{ fontSize: 13 }} />
            <span>Modelo WhatsApp</span>
          </span>
        </Tooltip>
      </div>

      {/* Botões Interativos */}
      {buttons.length > 0 && (
        <div className={classes.buttonsContainer}>
          <Divider style={{ margin: '4px 0' }} />
          {buttons.map((btn, index) => {
            if (btn.type === 'URL') {
              return (
                <button
                  key={`btn-url-${index}`}
                  className={classes.actionButton}
                  onClick={() => handleOpenUrl(btn.url)}
                  title={btn.url}
                >
                  <Language style={{ fontSize: 18 }} />
                  <span>{btn.text}</span>
                  <Launch style={{ fontSize: 14, marginLeft: 'auto', opacity: 0.6 }} />
                </button>
              );
            }

            if (btn.type === 'PHONE_NUMBER') {
              return (
                <button
                  key={`btn-phone-${index}`}
                  className={classes.actionButton}
                  onClick={() => handleCall(btn.phone_number)}
                  title={btn.phone_number}
                >
                  <Phone style={{ fontSize: 18 }} />
                  <span>{btn.text}</span>
                </button>
              );
            }

            if (btn.type === 'COPY_CODE') {
              const isCopied = copiedIndex === index;
              return (
                <button
                  key={`btn-copy-${index}`}
                  className={classes.actionButton}
                  onClick={() => handleCopyCode(btn.code, index)}
                  title={`Copiar: ${btn.code}`}
                >
                  {isCopied ? (
                    <>
                      <CheckCircleOutline style={{ fontSize: 18, color: '#10B981' }} />
                      <span style={{ color: '#10B981' }}>Copiado!</span>
                    </>
                  ) : (
                    <>
                      <FileCopyOutlined style={{ fontSize: 18 }} />
                      <span>{btn.text}</span>
                    </>
                  )}
                </button>
              );
            }

            if (btn.type === 'LIST') {
              return (
                <button
                  key={`btn-list-${index}`}
                  className={classes.actionButton}
                  onClick={() => handleOpenListDialog(btn)}
                >
                  <ListOutlined style={{ fontSize: 18 }} />
                  <span>{btn.text}</span>
                </button>
              );
            }

            if (btn.type === 'PIX') {
              const pixCode = btn.params?.payment_settings?.[0]?.pix_static_code?.key || btn.params?.pix_code || '';
              return (
                <div key={`btn-pix-${index}`} className={classes.pixCard}>
                  <div className={classes.pixTitle}>
                    <Payment style={{ fontSize: 18 }} />
                    <span>Pagamento PIX</span>
                  </div>
                  {btn.params?.total_amount && (
                    <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                      Valor: R$ {(btn.params.total_amount.value / 100).toFixed(2)}
                    </Typography>
                  )}
                  {pixCode && (
                    <button
                      className={classes.actionButton}
                      onClick={() => handleCopyCode(pixCode, `pix-${index}`)}
                      style={{ marginTop: 6 }}
                    >
                      <FileCopyOutlined style={{ fontSize: 16 }} />
                      <span>Copiar Chave PIX</span>
                    </button>
                  )}
                </div>
              );
            }

            // Quick reply padrão
            return (
              <button
                key={`btn-qr-${index}`}
                className={classes.actionButton}
                onClick={() => handleQuickReply(btn.text)}
              >
                <TouchApp style={{ fontSize: 18 }} />
                <span>{btn.text}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Modal para Menus de Seleção / Listas */}
      {selectedList && (
        <Dialog
          open={listDialogOpen}
          onClose={() => setListDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedList.text || 'Opções'}</span>
            <CloseIcon
              style={{ cursor: 'pointer' }}
              onClick={() => setListDialogOpen(false)}
            />
          </DialogTitle>
          <DialogContent dividers style={{ padding: 0 }}>
            {selectedList.sections && selectedList.sections.length > 0 ? (
              selectedList.sections.map((section, sIdx) => (
                <div key={`sec-${sIdx}`}>
                  {section.title && (
                    <div className={classes.dialogSectionHeader}>
                      {section.title}
                    </div>
                  )}
                  <List disablePadding>
                    {section.rows && section.rows.map((row, rIdx) => (
                      <ListItem
                        button
                        key={`row-${sIdx}-${rIdx}`}
                        onClick={() => {
                          handleQuickReply(row.title);
                          setListDialogOpen(false);
                        }}
                      >
                        <ListItemText
                          primary={row.title}
                          secondary={row.description}
                          primaryTypographyProps={{ style: { fontWeight: 600, fontSize: '0.88rem' } }}
                          secondaryTypographyProps={{ style: { fontSize: '0.78rem' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </div>
              ))
            ) : (
              <Box p={3} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  Nenhuma opção disponível no momento.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setListDialogOpen(false)} color="primary">
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default Template;
